// The site's emoji (PLAN-2026-10-27-more-fun-in-chat C1, scenarios I1 and I2).
//
// ONE SET FOR EVERY CLIENT AND THE SERVER: the picker beside the chat box draws
// these groups, `:name:` typed in the box becomes its emoji, and a reaction is
// any of them (`ChatReactRequest`), so a client that draws its own picker
// offers what the server takes.
//
// CURATED, NOT EVERY EMOJI: a picker of a few hundred a person can page
// through, game day first, and nothing a family league would mind (DIRECTION
// A15) — no middle finger, nothing for money. A phone's own keyboard still
// types whatever it has into a message; the set bounds the picker and the
// reactions, never the words.
//
// Each line is the emoji, its name (the `:name:` that types it), and the words
// that find it in the picker's search.

export interface ChatEmoji {
  char: string;
  /** Lowercase, `_` between words: `:fire:` types 🔥. Unique across the set. */
  name: string;
  /** Other words the search finds it by. */
  words: readonly string[];
}

export interface ChatEmojiGroup {
  id: string;
  /** What the group's tab is called, to a screen reader and in a tooltip. */
  label: string;
  /** The emoji that stands for the group on its tab. */
  icon: string;
  emoji: readonly ChatEmoji[];
}

const GAME_DAY = `
🏈 football ball game nfl
🏆 trophy champion win title
🥇 first_place gold medal win
🥈 second_place silver medal
🥉 third_place bronze medal
🏅 medal award
🎯 bullseye target accurate
🔥 fire hot lit flame
💯 hundred perfect 100
💪 muscle strong flex
🐐 goat greatest best
👑 crown king queen
🚀 rocket boom launch
📈 chart_up up rising
📉 chart_down down falling
🧊 ice cold clutch
🚑 ambulance injury hurt
🩼 crutch injury hurt
🤕 hurt injury bandage
🗑️ wastebasket trash garbage
🚽 toilet toilet_bowl last
🧻 toilet_paper roll last
🧂 salt salty
🪑 chair bench sit
🛋️ couch bench sit
⏰ alarm_clock time late
🔒 lock locked lock_it_in
🎲 dice luck random
🪦 headstone dead rip grave
⚰️ coffin dead rip
🫡 salute respect
🙏 pray please hope
🎉 tada party celebrate
🍿 popcorn watching drama
📺 tv watch game
🧠 brain smart genius
🤡 clown fool
🏁 checkered_flag finish done
🚨 siren alert rotating_light
📣 megaphone loud announce
⚡ zap lightning fast
🌪️ tornado chaos
💣 bomb boom
🧨 firecracker dynamite
🏟️ stadium field
`;

const FACES = `
😀 grinning smile happy
😃 smiley happy
😄 smile happy
😁 grin happy teeth
😆 laughing lol haha
😅 sweat_smile phew nervous
🤣 rofl lol laughing rolling
😂 joy lol laughing tears
🙂 slight_smile smile
🙃 upside_down silly sarcasm
😉 wink
😊 blush happy
😇 innocent halo angel
🥰 smiling_hearts love
😍 heart_eyes love
🤩 star_struck wow
😘 kiss love
😋 yum tasty
😜 winking_tongue silly
🤪 zany crazy silly
🤗 hugging hug
🤭 hand_over_mouth oops giggle
🤫 shush quiet secret
🤐 zipper_mouth quiet
🤔 thinking hmm think
🤨 raised_eyebrow suspicious skeptical
😐 neutral meh
😑 expressionless meh blank
😶 no_mouth speechless
😏 smirk smug
😒 unamused meh annoyed
🙄 eye_roll whatever
😬 grimace yikes awkward
😮‍💨 exhale sigh relief
😌 relieved phew calm
😔 pensive sad
🤤 drool want
😴 sleeping zzz asleep sleeper
🤢 nauseated sick gross
🤮 vomit sick gross
🥵 hot_face hot
🥶 cold_face cold freezing
🥴 woozy dizzy
😵‍💫 dizzy_face dizzy
🤯 mind_blown wow exploding
🤠 cowboy yeehaw
🥳 partying party celebrate
😎 sunglasses cool
🤓 nerd geek
🧐 monocle inspect hmm
😕 confused huh
😟 worried
🙁 frown sad
😮 open_mouth wow surprised
😲 astonished shock wow
😳 flushed embarrassed
🥺 pleading please puppy
🥹 holding_tears moved proud
😨 fearful scared
😰 anxious nervous sweat
😢 cry sad tear
😭 sob crying sad
😱 scream shock omg
😖 confounded ugh
😞 disappointed sad
😩 weary tired ugh
😫 tired ugh
🥱 yawn boring tired
😤 triumph huff steam
😡 rage angry mad
😠 angry mad
😈 smiling_imp devil evil
💀 skull dead lol dying
☠️ skull_crossbones dead danger
💩 poop
👻 ghost boo
👽 alien ufo
🤖 robot bot
🫠 melting melt dying
🫣 peeking peek scared
`;

const HANDS = `
👍 thumbs_up yes like good +1
👎 thumbs_down no dislike bad -1
👏 clap applause bravo
🙌 raised_hands hooray yay
🤝 handshake deal agree trade
✌️ peace victory
🤞 fingers_crossed luck hope
🤟 love_you love
🤘 rock_on metal
👌 ok_hand ok perfect
🤌 pinched chef kiss
👈 point_left left
👉 point_right right
👆 point_up up
👇 point_down down
☝️ index_up one wait
✋ raised_hand stop high_five
👋 wave hi bye hello
🤙 call_me shaka
✊ fist power
👊 punch fist_bump
🫶 heart_hands love
🫵 point_at_you you
👀 eyes look watching
🙈 see_no_evil monkey
🙊 speak_no_evil oops monkey
💅 nail_polish sassy unbothered
`;

const HEARTS = `
❤️ heart love red
🧡 orange_heart love
💛 yellow_heart love
💚 green_heart love
💙 blue_heart love
💜 purple_heart love
🖤 black_heart love
🤍 white_heart love
💔 broken_heart heartbreak sad
❤️‍🔥 heart_on_fire love passion
✨ sparkles shiny new
⭐ star favorite
🌟 glowing_star shine
💫 dizzy stars
💥 boom collision explosion
💢 anger mad
💬 speech_bubble talk
💤 zzz sleep
✅ check yes done
❌ cross no wrong
⚠️ warning caution
🚫 no_entry forbidden no
❗ exclamation bang
❓ question huh
‼️ bangbang
⁉️ interrobang
🆗 ok_button ok
🆒 cool_button cool
🆕 new_button new
🎶 notes music
➕ plus add
➖ minus
`;

// A MASCOT FOR EVERY NICKNAME A LEAGUE THROWS AROUND: the animals, and the
// marks people use for the pro clubs (a pirate flag, a cheese).
const MASCOTS = `
🦅 eagle eagles seahawks bird
🐦 bird cardinals falcons ravens
🐻 bear bears
🦁 lion lions
🐯 tiger bengals
🐆 leopard jaguars panthers
🐎 horse broncos colts
🐏 ram rams
🐬 dolphin dolphins
🦬 bison bills buffalo
🦈 shark trade_shark
🐺 wolf
🦊 fox
🐶 dog dawg browns
🐱 cat
🐷 pig
🐸 frog
🐔 chicken scared
🦃 turkey thanksgiving
🐢 turtle slow
🐌 snail slow
🦖 t_rex dinosaur old
🦄 unicorn
🐝 bee
🐍 snake traitor
🏴‍☠️ pirate_flag buccaneers raiders pirate
⚔️ crossed_swords vikings titans battle
🛡️ shield
🏹 bow_and_arrow chiefs
🧀 cheese packers cheesehead
⚜️ fleur_de_lis saints
⛏️ pick 49ers miners
✈️ airplane jets
⚙️ gear steelers
🌊 wave_water seahawks ocean
`;

const FOOD = `
🍕 pizza
🍔 burger hamburger
🌭 hot_dog
🌮 taco tuesday
🍗 drumstick chicken wings
🍖 meat bbq
🥩 steak
🍟 fries
🍺 beer
🍻 beers cheers
🥂 clink cheers toast
🍾 champagne celebrate
🥤 soda drink
☕ coffee monday
🍩 donut doughnut zero
🎂 cake birthday
🥧 pie
🍪 cookie
🥨 pretzel
🎊 confetti party
🎈 balloon party
🎁 gift present
`;

const THINGS = `
📊 bar_chart stats
🧮 abacus math
🔮 crystal_ball prediction future
🧲 magnet waivers
🪄 magic_wand magic
📝 memo note
📰 newspaper news
📅 calendar date week
⏳ hourglass waiting time
🔔 bell notify
💡 bulb idea
🔑 key
🧹 broom sweep
🧯 extinguisher put_out
🪓 axe cut drop
✂️ scissors cut drop
🔨 hammer veto
📦 package trade deal
📌 pushpin pin
🌧️ rain weather
❄️ snowflake snow cold
☀️ sun sunny
🎮 video_game game
🎤 microphone mic_drop
🎬 clapper action
`;

function parse(table: string): ChatEmoji[] {
  return table
    .trim()
    .split('\n')
    .map((line) => {
      const [char, name, ...words] = line.trim().split(/\s+/);
      return { char: char!, name: name!, words };
    });
}

export const CHAT_EMOJI_GROUPS: readonly ChatEmojiGroup[] = [
  { id: 'game-day', label: 'Game day', icon: '🏈', emoji: parse(GAME_DAY) },
  { id: 'faces', label: 'Faces', icon: '😀', emoji: parse(FACES) },
  { id: 'hands', label: 'Hands', icon: '👍', emoji: parse(HANDS) },
  { id: 'hearts', label: 'Hearts and marks', icon: '❤️', emoji: parse(HEARTS) },
  { id: 'mascots', label: 'Mascots', icon: '🦅', emoji: parse(MASCOTS) },
  { id: 'food', label: 'Food and party', icon: '🍕', emoji: parse(FOOD) },
  { id: 'things', label: 'Things', icon: '🔮', emoji: parse(THINGS) },
];

const ALL: readonly ChatEmoji[] = CHAT_EMOJI_GROUPS.flatMap((g) => g.emoji);
const BY_CHAR = new Map(ALL.map((e) => [e.char, e]));
const BY_NAME = new Map(ALL.map((e) => [e.name, e]));

/**
 * Every emoji of the set, in the picker's order: what a reaction may be
 * (`ChatReactRequest`). A tuple, so a schema can say so.
 */
export const CHAT_EMOJI = ALL.map((e) => e.char) as [string, ...string[]];

/** The emoji of the set this character is, or undefined. */
export function chatEmoji(char: string): ChatEmoji | undefined {
  return BY_CHAR.get(char);
}

/** `fire` → 🔥: the emoji a `:name:` types, or undefined. */
export function emojiNamed(name: string): ChatEmoji | undefined {
  return BY_NAME.get(name.toLowerCase());
}

/**
 * The emoji a search finds, best first: the name exactly, then a name that
 * begins with it, then a word that does, then a name that holds it, and the
 * shorter name first within each (`:fi` is 🔥 before 🥇 *first_place*). What
 * the picker's search and `:` typed in the box both offer.
 */
export function searchEmoji(query: string, limit = 60): ChatEmoji[] {
  const q = query.trim().toLowerCase().replace(/^:|:$/g, '').replace(/\s+/g, '_');
  if (q === '') return [];
  const rank = (e: ChatEmoji): number =>
    e.name === q
      ? 0
      : e.name.startsWith(q)
        ? 1
        : e.words.some((w) => w.startsWith(q))
          ? 2
          : e.name.includes(q)
            ? 3
            : -1;
  return ALL.map((e, i) => ({ e, r: rank(e), i }))
    .filter((x) => x.r >= 0)
    .sort((a, b) => a.r - b.r || a.e.name.length - b.e.name.length || a.i - b.i)
    .slice(0, limit)
    .map((x) => x.e);
}

let emojiRun: RegExp | null = null;

/**
 * How many emoji a message is when it is nothing else — one to three are
 * drawn large, as every chat app does — and 0 when it holds anything but
 * emoji and spaces. Any emoji counts, not only the set's: a phone types its own.
 *
 * Built on first use, as the masking's pattern is (`mask.ts`), so an engine
 * that cannot build it fails here rather than when the app loads.
 */
export function emojiOnly(body: string): number {
  emojiRun ??= new RegExp(
    String.raw`\p{Regional_Indicator}{2}|[#*0-9]️?⃣|\p{Extended_Pictographic}(?:️|\p{Emoji_Modifier})?(?:‍\p{Extended_Pictographic}(?:️|\p{Emoji_Modifier})?)*`,
    'gu',
  );
  const text = body.trim();
  if (text === '') return 0;
  const found = text.match(emojiRun) ?? [];
  const rest = text.replace(emojiRun, '').replace(/\s/g, '');
  return rest === '' ? found.length : 0;
}
